// mission-initializer-tickets.ts
//
// Split off mission-initializer.ts (≤200 line budget): seeds survey (brownfield
// only) + plan bootstrap tickets with idempotency keys (spec §4 "Bootstrap",
// test 9). Ticket kind enum/shape validated against TicketSchema (P4).

import { promises as fs } from "node:fs";
import { join } from "node:path";
import { ensureDir } from "../filesystem-helpers.js";
import { type Ticket, TicketSchema } from "./schemas/ticket-schema.js";
import { ticketFileName } from "./ticket-file-name.js";

function seedTicket(overrides: Partial<Ticket> & Pick<Ticket, "id" | "title" | "kind">): Ticket {
  return TicketSchema.parse({
    revision: 1,
    executor: "claude",
    stage: overrides.kind === "survey" ? "SURVEYING" : "PLANNING",
    slice_id: "bootstrap",
    // Callers set the plan ticket's dependency by mode (SOL-03) — a default
    // pointing at survey T-0001 left greenfield's plan waiting forever.
    depends_on: [],
    command_adapter: `${overrides.kind}-v1`,
    plan_path: ".avatar/PLAN.md",
    phase_path: ".avatar/plans/bootstrap.md",
    allowed_paths: [],
    verification_recipe: `${overrides.kind}-bootstrap`,
    required_capabilities: overrides.kind === "survey" ? ["local_test"] : [],
    sources: [],
    ...overrides,
  });
}

/**
 * Seed survey (brownfield only) + plan bootstrap tickets into the execution
 * repo's `.avatar/tickets/` directory. Idempotency: each ticket's id (and
 * hence its filename/idempotency key) is derived purely from its position in
 * the bootstrap sequence, not from any operation timestamp — re-running seed
 * for the SAME mission overwrites the same files rather than duplicating.
 */
export async function seedBootstrapTickets(
  executionRepoPath: string,
  mode: "greenfield" | "brownfield",
): Promise<Ticket[]> {
  const ticketsDir = join(executionRepoPath, ".avatar", "tickets");
  await ensureDir(ticketsDir);

  const tickets: Ticket[] = [];
  if (mode === "brownfield") {
    tickets.push(
      seedTicket({
        id: "T-0001",
        title: "Survey codebase",
        kind: "survey",
        command_adapter: "survey-v1",
      }),
    );
  }
  // SOL-03 (audit 2026-09-28): plan waits on survey ONLY when survey was seeded.
  // Greenfield has no T-0001, so a dependency on it never resolves.
  tickets.push(
    seedTicket({
      id: "T-0002",
      title: "Generate plan",
      kind: "plan",
      command_adapter: "plan-v1",
      depends_on: mode === "brownfield" ? ["T-0001"] : [],
    }),
  );
  // P20: `review-local-v1` is deliberately NOT a bootstrap ticket. It reviews
  // the CANDIDATE — the commit the mission is about to push — so it can only
  // run once the plan's own tickets are done. A ticket seeded here can only
  // depend on tickets that exist NOW, so `depends_on: ["T-0002"]` makes it
  // ready right after planning, ahead of every ticket the plan produces:
  // measured on a real mission, the report was promoted at SHA 1a8d9a18 while
  // the release flow looked it up at 7cfac65a. It is dispatched instead from
  // review-candidate-session.ts, at the point the DAG is exhausted.
  for (const ticket of tickets) {
    await fs.writeFile(
      join(ticketsDir, ticketFileName(ticket)),
      `${JSON.stringify(ticket, null, 2)}\n`,
      "utf8",
    );
  }
  return tickets;
}
